import React, { useRef, useState } from "react";
import { ShieldAlert, Loader2, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../ui/dialog";

const CATEGORIES = [
  { value: "harassment", label: "Harassment" },
  { value: "unwanted_calls", label: "Unwanted / repeated calls" },
  { value: "rude_behaviour", label: "Rude behaviour" },
  { value: "safety", label: "Safety concern" },
  { value: "fraud", label: "Fraud / scam" },
  { value: "other", label: "Something else" },
];

const MAX_PHOTOS = 3;

// Lets a customer report misconduct by a partner on a given order/ride/booking.
export default function ReportDialog({ contextType, contextId, triggerLabel = "Report a problem", triggerClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const addFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      if (photos.length >= MAX_PHOTOS) { toast.error(`Up to ${MAX_PHOTOS} photos.`); break; }
      if (!f.type.startsWith("image/")) continue;
      if (f.size > 10 * 1024 * 1024) { toast.error("Each image must be under 10 MB."); continue; }
      const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      const base64 = String(dataUrl).split(",")[1] || "";
      setPhotos((p) => p.length >= MAX_PHOTOS ? p : [...p, { file_data: base64, mime: f.type, preview: dataUrl }]);
    }
  };

  const submit = async () => {
    if (!category) return toast.error("Please choose what happened");
    setBusy(true);
    try {
      const { reportsApi } = await import("../../lib/api");
      await reportsApi.file({ context_type: contextType, context_id: contextId, category, description, photos: photos.map((p) => ({ file_data: p.file_data, mime: p.mime })) });
      toast.success("Report submitted. Our team will review it and take action.");
      setOpen(false); setCategory(""); setDescription(""); setPhotos([]);
    } catch (e) {
      const { errorMessage } = await import("../../lib/api");
      toast.error(errorMessage(e, "Could not submit report"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className={`rounded-full border-red-200 text-red-700 hover:bg-red-50 ${triggerClassName}`} data-testid="report-open">
          <ShieldAlert className="mr-2 h-4 w-4" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle>Report a problem</DialogTitle>
          <DialogDescription>Tell us what happened. Serious or repeated issues can lead to the partner being suspended.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="report-categories">
            {CATEGORIES.map((c) => (
              <button key={c.value} type="button" onClick={() => setCategory(c.value)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${category === c.value ? "bg-red-600 text-white" : "bg-surface-100 text-ink-soft ring-1 ring-surface-300"}`}
                data-testid={`report-cat-${c.value}`}>{c.label}</button>
            ))}
          </div>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Add details (optional) — what was said or done, when, etc." data-testid="report-description" />
          <div>
            <p className="mb-2 text-xs font-medium text-ink-soft">Attach photos or screenshots (optional)</p>
            <div className="flex flex-wrap items-center gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p.preview} alt="evidence" className="h-16 w-16 rounded-lg border border-surface-200 object-cover" />
                  <button type="button" onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 rounded-full bg-ink p-0.5 text-white" data-testid={`report-photo-remove-${i}`}><X className="h-3 w-3" /></button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <button type="button" onClick={() => fileRef.current?.click()} className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-surface-300 text-ink-muted hover:bg-surface-50" data-testid="report-photo-add"><ImagePlus className="h-5 w-5" /></button>
              )}
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={addFiles} data-testid="report-photo-input" />
            </div>
          </div>
          <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-red-600 text-white hover:bg-red-700" data-testid="report-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldAlert className="mr-2 h-4 w-4" />} Submit report
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
